/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {AfterViewChecked, AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {StatisticsProvider} from 'src/app/providers';
import {GoalData, GoalType, Statistics, StatisticsChart, StatisticsChartData} from 'src/app/model/statistics';
import {ModeService, UserService} from '../../../services';
import {SystemType} from '../../../model/enums/system.type';
import {Router} from '@angular/router';
import * as Chart from 'chart.js';
import * as moment from 'moment';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {GetCompletedReportModal} from '../../modal';
import {Helpers, User} from 'src/app/model';
import {Subject} from 'rxjs';
import {StatStageType} from 'src/app/model/enums/stat.stage.type';
import {LogService} from 'src/app/services';


@Component({
  selector: 'page-statistics',
  templateUrl: 'statistics.page.html',
  styleUrls: ['statistics.page.scss']
})
export class StatisticsPage implements OnInit, AfterViewChecked, OnDestroy, AfterViewInit {
  currentMode: SystemType;
  modeSub: any;
  statistics: Statistics;
  chartLoading: boolean = false;
  loading: boolean = false;
  offset: number = 0;
  currentDate: Date = new Date();
  // d3 chart variables
  chartRender = false;
  engagedData: StatisticsChart[] = [];
  completedData: StatisticsChart[] = [];
  modalRef: BsModalRef;
  StatStageType = StatStageType;
  currentUser: User;
  private currentUserSub: any;
  SystemType = SystemType;

  private margin = {top: 20, right: 30, bottom: 30, left: 30};
  private width: number;
  private height: number;
  private x: any;
  private y: any;
  private svg: any;
  private line: any;
  xAxis: any;
  yAxis: any;
  @ViewChild('barChart') private chartContainer: ElementRef;

  canvas: any;
  ctx: any;

  statiscticBackColor: string[] = [];
  statiscticBorderColor: string[] = [];
  statiscticData: number[] = [];
  statiscticLabel: string[] = [];
  editGoalLabel = 'Edit';
  newGoal: GoalData;
  editQuarterlyGoalLabel = 'Edit';
  quarterlyGoal: GoalData;

  constructor(
    private statisticsProvider: StatisticsProvider,
    private modeService: ModeService,
    private router: Router,
    private modalService: BsModalService,
    private userService: UserService,
    private logService : LogService
  ) {
    this.width = 1200 - this.margin.left - this.margin.right;
    this.height = 500 - this.margin.top - this.margin.bottom;
  }

  ngOnInit(): void {
    this.currentUserSub = this.userService.user$.subscribe(currentUser => this.currentUser = currentUser);
    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
    this.offset = 0;
    this.loadStatistics();
    this.loadPredictedStatistics();
  }

  ngAfterViewChecked(): void {
    if (!this.loading && this.statistics && this.chartRender) {
      this.chartRender = false;
    }
  }

  ngAfterViewInit(): void{
  }

  ngOnDestroy(): void {
    if (this.modeSub) this.modeSub.unsubscribe();
    if(this.currentUserSub) this.currentUserSub.unsubscribe();
  }

  loadStatistics(): void {
    this.loading = true;
    this.statisticsProvider.getStatistics(this.currentMode, this.offset).subscribe(data => {
      if (data.success) {
        this.statistics = new Statistics(data.result);
      }
      this.loading = false;
    }, error => {
      this.logService.error(error);
      this.loading = false;
    });
  }

  refresh(): void{
    this.offset = 0;
    this.editGoalLabel = 'Edit';
    this.loadStatistics();
  }

  onPrevious(): void {
    this.offset = this.offset - 1;
    this.loadStatistics();
  }

  onNext(): void {
    this.offset = this.offset + 1;
    this.loadStatistics();
  }

  getEngagedPercentage(): number {
    let result = 0;

    if(this.offset === 0){
      const lastWeek: number = this.statistics.ENGAGED_LAST_WEEK.VALUE / 5;
      const thisWeek: number = this.statistics.ENGAGED_THIS_WEEK.VALUE / moment(this.currentDate).day();

      result = (thisWeek - lastWeek) / thisWeek;

    }
    else {
      const lastWeek: number = this.statistics.ENGAGED_LAST_WEEK.VALUE;
      const thisWeek: number = this.statistics.ENGAGED_THIS_WEEK.VALUE;
      result = (thisWeek - lastWeek) / thisWeek;
    }


    return result;
  }

  getCompletedPercentage(): number {
    let result = 0;
    if (this.offset === 0) {
      const lastWeek: number = this.statistics.COMPLETED_LAST_WEEK / 5;
      const thisWeek: number = this.statistics.COMPLETED_THIS_WEEK / moment(this.currentDate).day();

      result = (thisWeek - lastWeek) / thisWeek;

    }
    else {
      const lastWeek: number = this.statistics.COMPLETED_LAST_WEEK;
      const thisWeek: number = this.statistics.COMPLETED_THIS_WEEK;
      result = (thisWeek - lastWeek) / thisWeek;
    }

    return result;
  }

  getAbsValue(value: number): number {
    return Math.abs(value);
  }

  isValidNumber(num: any): boolean {
    if (isNaN(num))
      return true;

    return false;
  }

  gotoDashboard(state: string): void {
    this.router.navigate(['/dashboard'], { queryParams: { stats: state } });
  }

  goToEngageView(event: any, isThisWeek: boolean = true): void{
    let start, end;
    if(isThisWeek){
      if (this.editGoalLabel != 'Edit') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      start = moment(this.statistics.THIS_WEEK_BEG).format('YYYY-MM-DD');
      end = moment(this.statistics.THIS_WEEK_END).format('YYYY-MM-DD');
      this.router.navigate(['/dashboard'], { queryParams: { stats: 'ENGAGED', week_start: start, week_end: end } });
    }
    else{
      start = moment(this.statistics.THIS_WEEK_BEG).subtract(7, 'days').format('YYYY-MM-DD');
      end = moment(this.statistics.THIS_WEEK_END).subtract(7, 'days').format('YYYY-MM-DD');
      this.router.navigate(['/dashboard'], { queryParams: { stats: 'ENGAGED', week_start: start, week_end: end } });
    }
  }

  setChartData(results: any): void {
    this.engagedData = [];
    this.completedData = [];
    for (let i = 0; i < results.DAILY_ENGAGED_DATA.length; i++) {
      this.engagedData.push({INDEX: i, VALUE: results.DAILY_ENGAGED_DATA[i].VALUE, DATE: results.DAILY_ENGAGED_DATA[i].DATE_CREATED});
    }
    for (let i = 0; i < results.DAILY_COMPLETED_DATA.length; i++) {
      this.completedData.push({INDEX: i, VALUE: results.DAILY_COMPLETED_DATA[i].VALUE, DATE: results.DAILY_COMPLETED_DATA[i].DATE_CREATED});
    }
  }

  loadPredictedStatistics(): void {
    this.chartLoading = true;
    this.statisticsProvider.getChartPredictedData(this.currentMode).subscribe(data => {
      this.chartLoading = false;
      if (data.success) {
        this.statiscticBackColor = [];
        this.statiscticBorderColor = [];
        this.statiscticData = [];
        this.statiscticLabel = [];

        let parsedData = StatisticsChartData.parse(data.result);

        parsedData.HISTORIC.map((data, i) => {
          this.statiscticData.push(data.VALUE);

          this.statiscticLabel.push(moment(data.DATE).format("MMM DD, YYYY"));

          if(Number(data.VALUE) >= 27.5){
            this.statiscticBackColor.push('rgba(0, 128, 0, 0.2)');
            this.statiscticBorderColor.push('rgba(0, 128, 0, 1)');
          }
          else {
            this.statiscticBackColor.push('rgba(255, 99, 132, 0.2)');
            this.statiscticBorderColor.push('rgba(255, 99, 132, 1)');
          }
        });

        parsedData.CURRENT.map((data, i) => {
          this.statiscticData.push(data.VALUE);

          this.statiscticLabel.push(`Current Week`);

          this.statiscticBackColor.push('rgba(0, 128, 0, 0.2)');
          this.statiscticBorderColor.push('rgba(0, 128, 0, 1)');
        });

        /* parsedData.PREDICTED.map((data, i) => {
          this.statiscticData.push(data.VALUE);

          this.statiscticLabel.push(moment(data.DATE).format("MMM DD, YYYY"));

          this.statiscticBackColor.push('rgba(54, 162, 235, 0.2)');
          this.statiscticBorderColor.push('rgba(54, 162, 235, 1)');
        }); */
      }

      this.drawChart();
    }, error => {
      this.logService.error(error);
      this.chartLoading = false;
    });
  }

  // Draw chart for historical and periodical data
  drawChart(): void{
    setTimeout(() => {
      this.canvas = document.getElementById('historicalChart');
      if(this.canvas){
        let data = {
          labels: this.statiscticLabel,
          datasets: [{
            label: 'Engaged Reports',
            data: this.statiscticData,
            backgroundColor: this.statiscticBackColor,
            borderColor: this.statiscticBorderColor,
            borderWidth: 1
          },]
        };

        this.ctx = this.canvas.getContext('2d');
        let myChart = new Chart(this.ctx, {
          type: 'bar',
          data: data,
          options: {
            responsive: false,
            scales: {
              yAxes: [{
                ticks: {
                  beginAtZero: true
                }
              }]
            },
            'animation': {
              'duration': 1,
              'onComplete': function(): void {
                var chartInstance = this.chart, ctx = chartInstance.ctx;

                ctx.font = Chart.helpers.fontString(Chart.defaults.global.defaultFontSize, Chart.defaults.global.defaultFontStyle, Chart.defaults.global.defaultFontFamily);
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';

                this.data.datasets.forEach(function(dataset, i) {
                  var meta = chartInstance.controller.getDatasetMeta(i);
                  meta.data.forEach(function(bar, index) {
                    var data = dataset.data[index];
                    ctx.fillText(data, bar._model.x, bar._model.y - 5);
                  });
                });
              }
            },
          }
        });
      }
    }, 1000);
  }

  viewReport(stage: StatStageType, offset: number): void {
    const config = Helpers.getModalConfig();
    config.class = 'modal-lg';
    this.modalRef = this.modalService.show(GetCompletedReportModal, config);
    const ackSubject = new Subject<boolean>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.currentMode = this.currentMode;
    this.modalRef.content.offset = offset;
    this.modalRef.content.stage = stage;
    this.modalRef.content.loadCompletedReports();

    ackSubject.subscribe(value => {
      if (value) {
      }
    });
  }

  onEditGoal(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.editGoalLabel == 'Edit') {
      this.editGoalLabel = 'Save';
      this.newGoal = new GoalData();
    }
    else {
      this.editGoalLabel = 'Edit';
      this.loading = true;
      this.newGoal.GOAL_TYPE = GoalType.WEEKLY;
      this.statisticsProvider.createGoal(this.currentMode, this.newGoal).subscribe(data => {
        this.loading = false;
        this.refresh();
      }, error => {
        this.logService.error(error);
        this.loading = false;
      });
    }
  }

  /** Edit quarterly goal */
  onEditGoalQuarterly(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.editQuarterlyGoalLabel == 'Edit') {
      this.editQuarterlyGoalLabel = 'Save';
      this.quarterlyGoal = new GoalData();
    }
    else {
      this.editQuarterlyGoalLabel = 'Edit';
      this.loading = true;
      this.quarterlyGoal.GOAL_TYPE = GoalType.QUARTERLY;
      this.statisticsProvider.createGoal(this.currentMode, this.quarterlyGoal).subscribe(data => {
        this.loading = false;
        this.refresh();
      }, error => {
        this.logService.error(error);
        this.loading = false;
      });
    }
  }

  checkGoal(): boolean {
    if (this.editGoalLabel == 'Edit')
      return false;
    return !(this.newGoal && this.newGoal.GOAL_TOTAL && this.newGoal.GOAL_REPORTS && this.newGoal.GOAL_AVG);
  }

  checkQuarterGoal(): boolean {
    if (this.editQuarterlyGoalLabel == 'Edit')
      return false;
    return !(this.quarterlyGoal && this.quarterlyGoal.GOAL_REPORTS);
  }

  isEditorAccess(): boolean {
    if(this.currentUser){
      if (this.currentUser.USER_ROLES.length === 0)
        return false;

      for (const r of this.currentUser.USER_ROLES)
        if (r.ROLE_PRIVILEGES.length > 0) {
          for (const p of r.ROLE_PRIVILEGES) {
            if (p.CODE === 'EDIT_STATISTICS_GOALS')
              return true;
          }
        }
    }

    return false;
  }
}
